import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { JobTrackingSyncQueueStatus } from '@acceligentllc/shared/enums/jobTrackingSyncQueue';
import { JobTrackingSyncQueueReason } from '@acceligentllc/shared/enums/jobTrackingSyncQueue';

import type { RootState } from 'af-reducers';

import DisabledFeatures from 'ab-common/environment/disabledFeatures';

import { Button } from '@acceligentllc/storybook';

import ConfirmationModal from 'af-components/ConfirmationModal';

import * as JobTrackingActions from 'af-actions/jobTracking';

import styles from './styles.module.scss';

interface OwnProps {
	refresh: () => void;
	jobId: number;
	syncStatus: Nullable<JobTrackingSyncQueueStatus>;
	syncReason: Nullable<JobTrackingSyncQueueReason>;
}

type Props = ConnectedProps<typeof connector> & OwnProps;

const MODAL_BODY = <div>
	<div className={styles['info-margin']}>Recalculation will start immediately and may take a few minutes depending on the size of the job.</div>
	<div>Refresh the page after a few minutes to see updated values.</div>
</div>;

const JobInSyncQueue: React.FC<Props> = (props) => {

	const {
		refresh,
		jobId,
		syncStatus,
		syncReason,
		triggerResync,
		isCompanyAdmin,
	} = props;

	const [resyncWarningModalOpen, setResyncWarningModalOpen] = React.useState(false);

	const openResyncModal = React.useCallback(() => {
		setResyncWarningModalOpen(true);
	}, []);

	const closeResyncModal = React.useCallback(() => {
		setResyncWarningModalOpen(false);
	}, []);

	const handleResyncConfirm = React.useCallback(async () => {
		closeResyncModal();
		await triggerResync(jobId);
		refresh();
	}, [closeResyncModal, jobId, refresh, triggerResync]);

	const syncStatusMessage: React.JSX.Element = React.useMemo(() => {
		if (syncReason === JobTrackingSyncQueueReason.TEMPLATE_CHANGE) {
			return <>
				<span className={`ic-TemporaryAgencyFilled text-blue ${styles.icon}`} />
				<div><strong>Job Tracking template was updated &mdash; recalculating data...</strong></div>
			</>;
		} else if (syncReason === JobTrackingSyncQueueReason.APPLY_TEMPLATE) {
			return <>
				<span className={`ic-MarkWarningFill text-orange ${styles.icon}`} />
				<div><strong>Adding template and calculating job tracking data</strong></div>
			</>;
		}

		return <>
			<span className={`ic-TemporaryAgencyFilled text-blue ${styles.icon}`} />
			<div><strong>Recalculating Job Tracking data...</strong></div>
		</>;
	}, [syncReason]);

	if (DisabledFeatures.JobTracking) {
		return (
			<div className={styles['no-template-container']}>
				No job tracking data available at this time.
			</div>
		);
	}

	return (
		<>
			<div className={styles['no-template-container']}>
				{syncStatusMessage}
				{ syncStatus !== JobTrackingSyncQueueStatus.IN_PROGRESS &&
					<span className={styles['info-margin']}>Updated values will appear within 24 hours.</span>
				}
				{ isCompanyAdmin && syncStatus !== JobTrackingSyncQueueStatus.IN_PROGRESS &&
					<Button
						icon="ArrowRotateRefresh"
						label="Recalculate Job Tracking"
						onClick={openResyncModal}
						style="link-danger"
					/>
				}
			</div>
			<ConfirmationModal
				body={MODAL_BODY}
				closeModal={closeResyncModal}
				confirmAction={handleResyncConfirm}
				confirmText={'Confirm'}
				modalStyle="warning"
				showModal={resyncWarningModalOpen}
				size="md"
				title={'Recalculate Job Tracking'}
			/>
		</>
	);
};

function mapDispatchToProps() {
	return {
		triggerResync: JobTrackingActions.triggerResync,
	};
}

function mapStateToProps(state: RootState) {
	const { userData, companyData } = state.user;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	return { isCompanyAdmin: companyData.isCompanyAdmin };
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(JobInSyncQueue);
